import {
  ICreateSavedFilterRequest,
  ISavedFilter,
  ISavedFiltersResponse,
  IUpdateSavedFilterRequest,
  SavedFilterType,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';
import { CacheTags } from '../../types/cacheTags';

// Org-scoped saved filters (invoice/contact/escalation tables) go through
// /saved-filters with the org header; user-scoped ones (reports, no org
// context) go through /user-saved-filters. Callers omit organisationId for
// user-scoped types.
function scopedRequest(organisationId: string | undefined, path: string) {
  if (organisationId) {
    return { url: `saved-filters${path}`, headers: { [orgHeader]: organisationId } };
  }

  return { url: `user-saved-filters${path}` };
}

export const savedFiltersApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchSavedFilters: builder.query<ISavedFiltersResponse, { organisationId?: string; type?: SavedFilterType }>({
      query: ({ organisationId, type }) => ({
        ...scopedRequest(organisationId, ``),
        method: `GET`,
        params: { type },
      }),
      providesTags: [{ type: CacheTags.SavedFilters, id: `LIST` }],
    }),
    createSavedFilter: builder.mutation<ISavedFilter, { organisationId?: string } & ICreateSavedFilterRequest>({
      query: ({ organisationId, ...body }) => ({
        ...scopedRequest(organisationId, ``),
        method: `POST`,
        data: body,
      }),
      invalidatesTags: [{ type: CacheTags.SavedFilters, id: `LIST` }],
    }),
    updateSavedFilter: builder.mutation<ISavedFilter, { organisationId?: string; id: string } & IUpdateSavedFilterRequest>({
      query: ({ organisationId, id, ...body }) => ({
        ...scopedRequest(organisationId, `/${id}`),
        method: `PATCH`,
        data: body,
      }),
      invalidatesTags: [{ type: CacheTags.SavedFilters, id: `LIST` }],
    }),
    deleteSavedFilter: builder.mutation<void, { organisationId?: string; id: string }>({
      query: ({ organisationId, id }) => ({
        ...scopedRequest(organisationId, `/${id}`),
        method: `DELETE`,
      }),
      invalidatesTags: [{ type: CacheTags.SavedFilters, id: `LIST` }],
    }),
  }),
});

export const {
  useFetchSavedFiltersQuery,
  useCreateSavedFilterMutation,
  useUpdateSavedFilterMutation,
  useDeleteSavedFilterMutation,
} = savedFiltersApi;
